import React, { useState } from 'react';
import { Crown } from 'lucide-react';
import { useSubscription } from '../../Contexts/SubscriptionContext';
import { createTrialUser, hasEmailUsedTrial } from '../../services/subscriptionService';
import { auth } from '../../firebase';
import PricingModal from './PricingModal';
import SubscriptionManagementModal from './SubscriptionManagementModal';

const SubscriptionStatus = ({ onStartTrial, currentUser }) => {
  const { subscription, getDisplayInfo, refreshSubscription } = useSubscription();
  const [showPricing, setShowPricing] = useState(false);
  const [showManagement, setShowManagement] = useState(false);
  
  const displayInfo = getDisplayInfo();
  const isTrialActive = displayInfo?.isTrialActive;
  const hasUsedTrial = !isTrialActive && (
    subscription?.subscriptionStatus === 'expired' ||
    subscription?.trialUsed ||
    localStorage.getItem('trial_used') === 'true'
  );
  const needsBillingAction = displayInfo?.needsBillingAction ?? ['past_due', 'unpaid', 'incomplete'].includes(subscription?.subscriptionStatus);

  const handleUpgradeClick = () => {
    // Show management modal for trial users, pricing modal for free users
    if (displayInfo.isTrialActive || subscription?.subscriptionStatus === 'expired' || subscription?.subscriptionStatus === 'cancelled' || displayInfo.isPremium || hasUsedTrial || needsBillingAction) {
      setShowManagement(true);
    } else {
      setShowPricing(true);
    }
  };

  const handleStartTrial = async () => {
    try {
      // Use the currentUser prop instead of auth.currentUser
      const userToUpgrade = currentUser || auth.currentUser;
      if (!userToUpgrade) {
        console.error('No authenticated user found');
        alert('Please sign in to start your trial.');
        return;
      }

      console.log('Starting trial for user:', userToUpgrade.uid || userToUpgrade.id);

      // Server-side check by email to prevent repeat trials
      if (userToUpgrade.email) {
        const emailAlreadyUsed = await hasEmailUsedTrial(userToUpgrade.email);
        if (emailAlreadyUsed || subscription?.subscriptionStatus === 'expired' || subscription?.trialUsed || localStorage.getItem('trial_used') === 'true') {
          try { localStorage.setItem('trial_used', 'true'); } catch {}
          setShowPricing(false);
          setShowManagement(true);
          return;
        }
      }
      
      // Upgrade user to trial
      const userId = userToUpgrade.uid || userToUpgrade.id;
      const userEmail = userToUpgrade.email;
      await createTrialUser(userId, userEmail);
      try { localStorage.setItem('trial_used', 'true'); } catch {}
      
      console.log('✅ Successfully upgraded to trial');
      
      // Refresh subscription data
      await refreshSubscription();
      
      // Close modals
      setShowPricing(false);
      setShowManagement(false);
      
    } catch (error) {
      console.error('Error starting trial:', error);
      
      // Show specific error message for trial abuse
      if (error.message.includes('already used a free trial')) {
        alert('⚠️ ' + error.message + '\n\nWould you like to subscribe to Premium instead?');
      } else {
        alert('Failed to start trial. Please try again.');
      }
    }
  };

  // Don't show anything if loading or no subscription data
  if (!subscription) return null;

  return (
    <>
      {/* Subscription Badge - Icterine with smoky black text */}
      <div className="flex items-center gap-2 px-4 py-2 rounded-lg shadow-lg text-sm font-medium cursor-pointer hover:opacity-90 transition-opacity bg-[color:var(--color-warn)] text-[color:var(--color-text)]" 
      style={{ width: '300px' }}
      onClick={handleUpgradeClick}>
        {displayInfo.isTrialActive && displayInfo.daysRemaining > 0 ? (
          <>
            <Crown className="w-4 h-4 flex-shrink-0" />
            <span className="font-medium whitespace-nowrap">
              Premium Trial • {displayInfo.daysRemaining === 1 ? '1 day left' : `${displayInfo.daysRemaining} days left`}
            </span>
          </>
        ) : subscription?.subscriptionStatus === 'expired' ? (
          <>
            <Crown className="w-4 h-4 flex-shrink-0" />
            <span className="font-medium">Trial expired</span>
          </>
        ) : (
          <>
            <Crown className="w-4 h-4 flex-shrink-0" />
            <span className="font-medium">{displayInfo.plan}</span>
          </>
        )}
      </div>

      {/* Pricing Modal for Free Users */}
      <PricingModal
        isOpen={showPricing}
        onClose={() => setShowPricing(false)}
        onStartTrial={handleStartTrial}
        userSubscription={subscription}
        hasUsedTrial={hasUsedTrial}
      />

      {/* Subscription Management Modal for Trial/Premium Users */}
      <SubscriptionManagementModal
        isOpen={showManagement}
        onClose={() => setShowManagement(false)}
        onStartTrial={handleStartTrial}
        userSubscription={subscription}
        currentUser={currentUser}
        refreshSubscription={refreshSubscription}
        hasUsedTrial={hasUsedTrial}
      />
    </>
  );
};

export default SubscriptionStatus;
